import React from "react";
import { type ApiApplication } from "../../api.ts";
import { Button } from "../../components/button.tsx";
import {
  buildTestStateValue,
  getTestRedirectUri,
} from "../../lib/test-redirect.ts";

interface TestActionProps {
  application: ApiApplication;
  ready: boolean;
  onAddTestUri: () => Promise<void> | void;
}

export function TestAction({
  application,
  ready,
  onAddTestUri,
}: TestActionProps) {
  if (!ready) {
    return (
      <p className="text-sm text-muted">
        Add an access code first so the test sign-in has a user to authenticate as.
      </p>
    );
  }

  const testRedirectUri = getTestRedirectUri();
  const isRegistered = application.redirectUris.includes(testRedirectUri);

  if (!isRegistered) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          The test sign-in URL has been removed from this application's
          redirect URIs. Re-add it to enable the test button.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="w-full sm:w-44"
          onClick={onAddTestUri}
        >
          Re-add test URL
        </Button>
      </div>
    );
  }

  const testUrl = buildAuthorizeUrl(
    application.issuerUrl,
    application.clientId,
    testRedirectUri,
    buildTestStateValue(application.id),
  );

  return (
    <div className="flex flex-col gap-3">
      <a
        href={testUrl}
        target="_blank"
        rel="noreferrer"
        className="w-full no-underline sm:w-44"
      >
        <Button variant="primary" size="md" className="w-full">
          Test sign-in →
        </Button>
      </a>
      <p className="text-sm text-muted">
        Opens the access-code prompt in a new tab. After you enter a valid
        code you'll land on a confirmation page on authed.co.
      </p>
    </div>
  );
}

function buildAuthorizeUrl(
  issuerUrl: string,
  clientId: string,
  redirectUri: string,
  state: string,
): string {
  const url = new URL(`${issuerUrl}/authorize`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid");
  url.searchParams.set("state", state);
  return url.toString();
}
